import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  Dropdown,
  DropdownButton,
  Form,
  Modal,
  Table,
  Badge
} from 'react-bootstrap';
import { FaUserPlus } from 'react-icons/fa';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import ApiService from '../../helpers/apiService';
import { v4 as uuidv4 } from 'uuid';
import { getEmailPresets, renderEmailTemplate } from '../message-templates/email-templates/baseTemplate';
import { useProject } from '../../contexts/ProjectContext';

interface Recipient {
  id?: string;
  value: string;
  label: string;
}

interface TemplateParameter {
  name: string;
  param: string;
  source?: string;       // kyc, users, manual, system
  resolveMethod?: string; // grpc_field, manual, generated, config
  description?: string;
  defaultValue?: string;
}

interface ChannelItem {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
}

interface SenderItem {
  id: string;
  channel_id: string;
  channel: string; // code канала
  name: string;
  config: Record<string, string>;
  is_active: boolean;
}

// Маппинг email-пресетов → UUID шаблонов в sender БД
const EMAIL_PRESET_TEMPLATE_IDS: Record<string, string> = {
  welcome: '00000000-0000-0000-0000-000000000001',
  kyc_approved: '00000000-0000-0000-0000-000000000002',
  deposit: '00000000-0000-0000-0000-000000000003',
  security_alert: '00000000-0000-0000-0000-000000000004',
  password_reset: '00000000-0000-0000-0000-000000000005',
  kyc_rejected: '00000000-0000-0000-0000-000000000006',
  withdrawal: '00000000-0000-0000-0000-000000000007',
  card_issued: '00000000-0000-0000-0000-000000000008',
  transaction_alert: '00000000-0000-0000-0000-000000000009',
  account_blocked: '00000000-0000-0000-0000-00000000000a',
};

// UUID шаблона для произвольного текста
const PLAIN_TEXT_TEMPLATE_ID = '00000000-0000-0000-0000-000000000010';

// UUID шаблона для SMS
const SMS_TEMPLATE_ID = '00000000-0000-0000-0000-000000000011';

// Названия email-пресетов для dropdown
const EMAIL_PRESET_LABELS: Record<string, string> = {
  welcome: 'Приветствие',
  kyc_approved: 'KYC подтверждён',
  kyc_rejected: 'KYC отклонён',
  deposit: 'Пополнение получено',
  withdrawal: 'Вывод обработан',
  card_issued: 'Карта выпущена',
  security_alert: 'Алерт безопасности',
  password_reset: 'Сброс пароля',
  transaction_alert: 'Алерт транзакции',
  account_blocked: 'Аккаунт заблокирован',
};

// v60.14 (2026-06-05): шаблоны подгружаются из sender через admin-api
// /notify/v1/template (response расширен Title + Type в admin-api commit 31b54ef).
// Раньше тут был хардкод TEXT_TEMPLATES — служил источником data для UI dropdown,
// но id передавался как мнемоник ("welcome"), worker ругался uuid.Parse.
// Теперь template.id = UUID из БД, template.title = мнемоник для отображения,
// template.type = канал (0=email, 1=sms, 2=push).
interface DbTemplate {
  id: string;        // UUID шаблона из sender.templates
  title: string;     // мнемоник ("welcome_push", "deposit")
  type: number;      // 0=email, 1=sms, 2=push
  content: string;   // body шаблона с %%param%% placeholder'ами
  params: Record<string, string>; // map name → description
}

// Канал → ожидаемый template.type. Используется для фильтрации списка шаблонов
// dropdown'a по выбранному "Виду отправки".
const CHANNEL_TO_TEMPLATE_TYPE: Record<string, number> = {
  email: 0,
  sms: 1,
  push: 2,
};

const MessageSender: React.FC = () => {
  const { currentProjectId, currentProject } = useProject();
  const assetsBaseUrl = (currentProject as any)?.project_data?.assets_base_url;
  const EMAIL_PRESETS = useMemo(() => getEmailPresets(assetsBaseUrl), [assetsBaseUrl]);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [sendType, setSendType] = useState<string>('');
  const [template, setTemplate] = useState<string>('');
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [sender, setSender] = useState<string>('');
  const [selectedSender, setSelectedSender] = useState<SenderItem | null>(null);
  const [message, setMessage] = useState<string>('');
  const [availableParameters, setAvailableParameters] = useState<TemplateParameter[]>([]);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [userList, setUserList] = useState<any[]>([]);
  const [userSearchInput, setUserSearchInput] = useState<string>('');
  const [userLoading, setUserLoading] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const userPageSize = 50;
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string>('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [selectedEmailPreset, setSelectedEmailPreset] = useState<string>('');

  // Каналы, отправители, параметры из БД
  const [channels, setChannels] = useState<ChannelItem[]>([]);
  const [senders, setSenders] = useState<SenderItem[]>([]);
  const [dbParams, setDbParams] = useState<any[]>([]);
  // v60.14: шаблоны подгружаются из sender через admin-api /notify/v1/template.
  // Раньше хардкод TEXT_TEMPLATES — теперь только fallback при недоступности sender.
  const [dbTemplates, setDbTemplates] = useState<DbTemplate[]>([]);

  const loadConfig = useCallback(async () => {
    try {
      const [chRes, sRes, pRes, tRes] = await Promise.all([
        ApiService.getChannels(),
        ApiService.getSenders(),
        ApiService.getNotifyParams(),
        ApiService.getTemplates().catch(() => ({ data: [] })),
      ]);
      setChannels((chRes.data || []).filter((c: ChannelItem) => c.is_active));
      setSenders((sRes.data || []).filter((s: SenderItem) => s.is_active));
      setDbParams(pRes.data || []);
      setDbTemplates(tRes.data || []);
    } catch (e) {
      console.error('Ошибка загрузки конфигурации:', e);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig, currentProjectId]);

  // Каналы для dropdown "Вид отправки"
  const channelOptions = useMemo(() => channels.map(c => c.name), [channels]);

  const selectedChannelCode = useMemo(() => {
    const ch = channels.find(c => c.name === sendType);
    return ch?.code || '';
  }, [channels, sendType]);

  const isEmailChannel = selectedChannelCode === 'email';
  const isSmsChannel = selectedChannelCode === 'sms';
  const senderEmail = selectedSender?.config?.email || 'noreply@taler.tirol';

  // Отправители, отфильтрованные по выбранному каналу
  const filteredSenders = useMemo(() => {
    if (!sendType) return senders;
    const ch = channels.find(c => c.name === sendType);
    if (!ch) return senders;
    return senders.filter(s => s.channel_id === ch.id);
  }, [sendType, senders, channels]);

  // v60.14: шаблоны, отфильтрованные по типу выбранного канала
  // (push=2, sms=1, email=0). Для email-канала dropdown показывает
  // email-пресеты (EMAIL_PRESETS, не БД-шаблоны) — здесь только text-flow.
  const filteredTemplates = useMemo(() => {
    const expectedType = CHANNEL_TO_TEMPLATE_TYPE[selectedChannelCode];
    if (expectedType === undefined) return dbTemplates;
    return dbTemplates.filter(t => t.type === expectedType);
  }, [dbTemplates, selectedChannelCode]);

  // UX: если для выбранного канала есть единственный отправитель — автоматически
  // подставляем его (запрос пользователя 2026-06-03). Если несколько — оператор
  // выбирает руками. Если ни одного — оставляем пустым.
  useEffect(() => {
    if (!sender && filteredSenders.length === 1) {
      const only = filteredSenders[0];
      setSender(only.name);
      setSelectedSender(only);
    }
  }, [filteredSenders, sender]);

  // Обработка выбора текстового шаблона (push/sms)
  // Разделяем параметры шаблона на manual (ввод оператора) и auto (резолвятся бэкендом)
  const enrichParams = (paramCodes: string[]) => {
    return paramCodes.map(code => {
      const db = dbParams.find((p: any) => p.code === code);
      return {
        name: db?.name || code,
        param: code,
        source: db?.source || 'manual',
        resolveMethod: db?.resolve_method || 'manual',
        description: db?.description || '',
        defaultValue: db?.default_value || '',
      };
    });
  };

  const handleTextTemplateChange = (eventKey: string | null) => {
    if (!eventKey) return;
    setSelectedEmailPreset('');
    setEmailPreviewHtml('');
    const t = dbTemplates.find(t => t.id === eventKey);
    if (!t) return;
    // eventKey = UUID из sender БД, не мнемоник. Передаётся напрямую в
    // senderSendPush — admin-api uuid.Parse'ит ок (resolvePushTemplateID
    // больше не нужен на этом пути, остаётся как backward-compat).
    setTemplate(eventKey);
    // Параметры — это keys map'ы template.params (например {name: "User name"}).
    setAvailableParameters(enrichParams(Object.keys(t.params || {})));
    setMessage(t.content);
    setParamValues({});
  };

  // Обработка выбора email-пресета
  const handleEmailPresetChange = (presetKey: string) => {
    setSelectedEmailPreset(presetKey);
    setTemplate(presetKey);
    const preset = EMAIL_PRESETS[presetKey];
    if (!preset) return;

    // Извлекаем %%param%% из всех полей пресета
    const allText = [preset.title, preset.description, preset.buttonText, preset.buttonUrl, preset.additionalText].filter(Boolean).join(' ');
    const matches = allText.match(/%%([^%]+)%%/g) || [];
    const params = [...new Set(matches.map(m => m.replace(/%%/g, '')))];
    setAvailableParameters(params.map(p => ({ name: p, param: p })));
    setMessage(`${preset.title}\n\n${preset.description}${preset.additionalText ? '\n\n' + preset.additionalText : ''}`);

    // Генерируем HTML-превью
    const html = renderEmailTemplate(preset);
    setEmailPreviewHtml(html);
  };

  // Подставить значения параметров в текст шаблона
  const resolveMessage = (text: string): string => {
    let resolved = text;
    for (const [key, val] of Object.entries(paramValues)) {
      if (val) resolved = resolved.replace(new RegExp(`%%${key}%%`, 'g'), val);
    }
    return resolved;
  };

  const fetchUsers = async (search?: string, page = 1) => {
    setUserLoading(true);
    try {
      // Бэкенд принимает phone (int64), email (string), userId (uuid) — не search
      const params: Record<string, any> = { page, size: userPageSize, enrichKyc: 'true' };
      let clientSearch = '';
      if (search) {
        const trimmed = search.trim();
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(trimmed)) {
          params.userId = trimmed;
        } else if (/^\+?\d{7,}$/.test(trimmed)) {
          params.phone = trimmed.replace('+', '');
        } else if (trimmed.includes('@')) {
          params.email = trimmed;
        } else {
          clientSearch = trimmed.toLowerCase();
        }
      }

      const data = await ApiService.get<{ data: any[]; total?: number }>('/user/v1/list', { params });
      let users = data.data || [];
      let total = data.total || users.length;

      // Клиентский поиск по имени если бэкенд не поддерживает текстовый поиск
      if (clientSearch) {
        users = users.filter((u: any) => {
          const name = (u.name || '').toLowerCase();
          const kycName = `${u.kyc_first_name || ''} ${u.kyc_last_name || ''}`.toLowerCase();
          return name.includes(clientSearch) || kycName.includes(clientSearch);
        });
        total = users.length; // корректируем total после клиентской фильтрации
      }

      // Сортируем: пользователи с контактами — первыми
      users.sort((a: any, b: any) => {
        const aHas = (a.phone || a.email || a.kyc_phone || a.kyc_email) ? 0 : 1;
        const bHas = (b.phone || b.email || b.kyc_phone || b.kyc_email) ? 0 : 1;
        return aHas - bHas;
      });

      setUserList(users);
      setUserTotal(total);
      setUserPage(page);
    } catch (e) {
      console.error('Ошибка при загрузке пользователей:', e);
      setUserList([]);
    } finally {
      setUserLoading(false);
    }
  };

  const handleSelectUser = async (user: any) => {
    const userId = user.userId || user.id;
    let email = user.email || user.kyc_email;
    let phone = user.phone || user.kyc_phone;
    let displayName = user.kyc_first_name
      ? `${user.kyc_first_name} ${user.kyc_last_name || ''}`.trim()
      : (user.name || userId);

    // Если контактов нет — дозапрашиваем по userId (бэкенд обогащает KYC)
    if (!email && !phone) {
      try {
        const detail = await ApiService.get<{ data: any[] }>('/user/v1/list', {
          params: { userId },
        });
        const u = detail.data?.[0];
        if (u) {
          email = u.email || u.kyc_email;
          phone = u.phone || u.kyc_phone;
          if (u.kyc_first_name) {
            displayName = `${u.kyc_first_name} ${u.kyc_last_name || ''}`.trim();
          }
        }
      } catch (e) {
        console.error('Ошибка при дозапросе пользователя:', e);
      }
    }

    if (isEmailChannel) {
      if (email && !recipients.some(r => r.value === email)) {
        setRecipients(prev => [...prev, { id: userId, value: email, label: `${displayName} <${email}>` }]);
      } else if (!email) {
        alert(`У пользователя ${displayName} нет email`);
      }
    } else {
      if (phone && !recipients.some(r => r.value === phone)) {
        setRecipients(prev => [...prev, { id: userId, value: phone, label: `${displayName} (${phone})` }]);
      } else if (!phone) {
        alert(`У пользователя ${displayName} нет телефона`);
      }
    }
    setShowUserModal(false);
  };

  const getUserIdByPhone = async (phone: string): Promise<string | null> => {
    try {
      const response = await ApiService.get<{ data: { id: string } }>(`/profile/v1/user_by_phone/${phone}`, { headers: { requestId: uuidv4() } });
      return response.data.id || null;
    } catch { return null; }
  };

  const sendMessage = async () => {
    if (!sendType || !sender) { alert('Пожалуйста, выберите вид отправки и отправителя'); return; }
    if (recipients.length === 0) { alert('Пожалуйста, выберите получателей'); return; }

    const channel = selectedSender?.channel || selectedChannelCode || 'push';
    setSending(true);

    try {
      for (const recipient of recipients) {
        // Валидация: email-канал → только email, sms → только телефон
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.value);
        const isPhone = /^\+?\d{7,}$/.test(recipient.value);
        if (channel === 'email' && !isEmail) {
          alert(`"${recipient.value}" — не email-адрес. Пропускаем.`);
          continue;
        }
        if (channel === 'sms' && !isPhone) {
          alert(`"${recipient.value}" — не номер телефона. Пропускаем.`);
          continue;
        }

        let userId: any = recipient.id;
        if (!userId) {
          userId = await getUserIdByPhone(recipient.value);
          if (!userId) { alert(`Не удалось найти пользователя с номером ${recipient.value}`); continue; }
        }

        const finalMessage = resolveMessage(message);

        if (channel === 'email' && selectedEmailPreset) {
          // Отправка email через sender с шаблоном из БД
          const templateId = EMAIL_PRESET_TEMPLATE_IDS[selectedEmailPreset];
          // Передаём заполненные значения параметров в sender-worker
          const emailParams: Record<string, string> = { ...paramValues };
          if (finalMessage) {
            emailParams['message'] = finalMessage;
          }
          await ApiService.senderSendEmail(
            templateId || PLAIN_TEXT_TEMPLATE_ID,
            recipient.value,
            senderEmail,
            emailParams,
            userId
          );
        } else if (channel === 'email') {
          await ApiService.senderSendEmail(
            PLAIN_TEXT_TEMPLATE_ID,
            recipient.value,
            senderEmail,
            { subject: 'Сообщение от Trientes', message: finalMessage },
            userId
          );
        } else if (channel === 'sms') {
          await ApiService.senderSendSms(
            SMS_TEMPLATE_ID,
            recipient.value,
            { message: finalMessage },
            userId
          );
        } else if (selectedChannelCode === 'push' && !template) {
          // Произвольный push (без template) — пока через notifications-сервис
          // (in-app feed). FCM push через sender требует обязательно template_id
          // — потому что worker определяет канал по template.Type (см.
          // CREL/sender/internal/service/worker.go case 2). Произвольные push
          // без шаблона можно покрыть позже отдельным flow в sender (RFC из бэклога).
          await ApiService.post('/notify/v1/send', {
            user_id: userId,
            channel: channel,
            title: 'Новое сообщение',
            message: finalMessage,
            link: 'https://gsmsoft.eu',
          }, { headers: { requestId: uuidv4() } });
        } else if (selectedChannelCode === 'push' && template) {
          // FCM push через sender pipeline (admin-api v60.9):
          // admin-api → Kafka sender_send (Type=2) → sender-worker → fcm.SendMulticast
          // → push на все активные FCM-токены user_id (DeviceTokenResolver).
          // Шаблон должен быть type=2 в sender.templates (например welcome_push).
          const templateData: Record<string, string> = {};
          availableParameters.forEach(p => { templateData[p.param] = paramValues[p.param] || ''; });
          await ApiService.senderSendPush(template, userId, templateData);
        } else {
          // Старый flow для не-push каналов через notifications-сервис
          // (in-app feed, gRPC stream к мобильному клиенту).
          const templateData: Record<string, string> = {};
          availableParameters.forEach(p => { templateData[p.param] = paramValues[p.param] || ''; });
          await ApiService.post('/notify/v1/send_template', {
            user_id: userId,
            channel: channel,
            template_name: template,
            data: templateData,
          }, { headers: { requestId: uuidv4() } });
        }
      }
      alert('Сообщение отправлено успешно');
    } catch (e) {
      console.error('Ошибка при отправке сообщения:', e);
      alert('Произошла ошибка при отправке сообщения');
    } finally {
      setSending(false);
    }
  };

  // Обработка смены типа — сбросить шаблон/отправителя/контент.
  // ВАЖНО: recipients НЕ сбрасываем — получатели одни и те же независимо от канала
  // (выбираются по user_id), валидация формата (email vs phone vs uuid) делается
  // в sendMessage() per-recipient. Сброс ломал UX: оператор выбирает получателя,
  // потом меняет канал — теряет выбор. Жалоба пользователя 2026-06-03.
  const handleSendTypeChange = (type: string) => {
    setSendType(type);
    setTemplate('');
    setSelectedEmailPreset('');
    setEmailPreviewHtml('');
    setAvailableParameters([]);
    setMessage('');
    setSender('');
    setSelectedSender(null);
  };

  const handleSenderChange = (senderName: string) => {
    setSender(senderName);
    const s = senders.find(s => s.name === senderName);
    setSelectedSender(s || null);
  };

  const handleUserSearch = () => {
    fetchUsers(userSearchInput, 1);
  };

  const totalPages = Math.max(1, Math.ceil(userTotal / userPageSize));

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h5 className="mb-0">Отправка сообщений</h5>
          <small className="text-muted">Push, SMS, Email и Telegram уведомления</small>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="row g-3">
            {/* Получатель */}
            <div className="col-12">
              <Form.Label className="small fw-semibold">Получатель</Form.Label>
              <div className="d-flex gap-2">
                <div className="flex-grow-1">
                  <CreatableSelect
                    isMulti
                    onChange={(opts: MultiValue<Recipient>) => setRecipients(opts ? [...opts] : [])}
                    value={recipients}
                    placeholder={isEmailChannel ? 'Введите email или выберите из списка' : 'Введите номер телефона или выберите из списка'}
                    formatCreateLabel={(v: string) => `Добавить "${v}"`}
                    isValidNewOption={(v: string) => !!v && (/^\+?\d+$/.test(v) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))}
                  />
                </div>
                <Button variant="outline-secondary" size="sm" onClick={() => { setShowUserModal(true); setUserSearchInput(''); setUserPage(1); fetchUsers('', 1); }}>
                  <FaUserPlus />
                </Button>
              </div>
            </div>

            {/* Вид отправки + Отправитель */}
            <div className="col-md-6">
              <Form.Label className="small fw-semibold">Вид отправки</Form.Label>
              <div>
                <DropdownButton title={sendType || 'Выберите тип'} variant="outline-secondary" size="sm" onSelect={v => v && handleSendTypeChange(v)}>
                  {channelOptions.map(t => (
                    <Dropdown.Item key={t} eventKey={t}>{t}</Dropdown.Item>
                  ))}
                </DropdownButton>
              </div>
            </div>

            <div className="col-md-6">
              <Form.Label className="small fw-semibold">Отправитель</Form.Label>
              <div>
                <DropdownButton title={sender || 'Выберите отправителя'} variant="outline-secondary" size="sm" onSelect={v => v && handleSenderChange(v)}>
                  {filteredSenders.map(s => (
                    <Dropdown.Item key={s.id} eventKey={s.name}>
                      {s.name}
                      <Badge bg="secondary" className="ms-2">{s.channel}</Badge>
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </div>
            </div>

            {/* Шаблон — зависит от канала */}
            <div className="col-md-6">
              <Form.Label className="small fw-semibold">
                Шаблон
                {isEmailChannel && <Badge bg="info" className="ms-1">Email</Badge>}
                {isSmsChannel && <Badge bg="warning" text="dark" className="ms-1">SMS</Badge>}
                {!isEmailChannel && !isSmsChannel && sendType && <Badge bg="secondary" className="ms-1">Push</Badge>}
              </Form.Label>
              <div>
                {isEmailChannel ? (
                  <DropdownButton
                    title={selectedEmailPreset ? (EMAIL_PRESET_LABELS[selectedEmailPreset] || selectedEmailPreset) : 'Выберите email-шаблон'}
                    variant="outline-secondary"
                    size="sm"
                    onSelect={v => v && handleEmailPresetChange(v)}
                  >
                    <Dropdown.Header>Email-шаблоны</Dropdown.Header>
                    {Object.entries(EMAIL_PRESET_LABELS).map(([key, label]) => (
                      <Dropdown.Item key={key} eventKey={key}>
                        <span>{label}</span>
                        <br />
                        <small className="text-muted">{key}</small>
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item eventKey="__custom__" onClick={() => { setSelectedEmailPreset(''); setEmailPreviewHtml(''); }}>
                      Произвольный текст
                    </Dropdown.Item>
                  </DropdownButton>
                ) : (
                  <DropdownButton
                    title={template ? (dbTemplates.find(t => t.id === template)?.title || 'Выбран шаблон') : 'Выберите шаблон'}
                    variant="outline-secondary"
                    size="sm"
                    onSelect={handleTextTemplateChange}
                  >
                    {filteredTemplates.length === 0 ? (
                      <Dropdown.Item disabled>
                        <small className="text-muted">Нет шаблонов для этого канала</small>
                      </Dropdown.Item>
                    ) : filteredTemplates.map(t => (
                      <Dropdown.Item key={t.id} eventKey={t.id}>
                        <span>{t.title || t.id}</span>
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item eventKey="__custom__" onClick={() => { setTemplate(''); setAvailableParameters([]); setMessage(''); }}>
                      Произвольный текст
                    </Dropdown.Item>
                  </DropdownButton>
                )}
              </div>
            </div>

            {/* Параметры шаблона */}
            {availableParameters.length > 0 && (
              <div className="col-12">
                <Form.Label className="small fw-semibold">Параметры шаблона</Form.Label>
                {/* Auto-параметры (резолвятся из сервисов по user_id) */}
                {availableParameters.some(p => p.resolveMethod !== 'manual') && (
                  <div className="mb-2">
                    <small className="text-muted">Авто (по user_id): </small>
                    {availableParameters.filter(p => p.resolveMethod !== 'manual').map(p => (
                      <Badge key={p.param} bg="success" className="me-1">
                        {p.param} <small>← {p.source}</small>
                      </Badge>
                    ))}
                  </div>
                )}
                {/* Manual-параметры (ввод оператора) */}
                <div className="row g-2">
                  {availableParameters.filter(p => p.resolveMethod === 'manual').map(p => (
                    <div key={p.param} className="col-md-4">
                      <Form.Control
                        size="sm"
                        placeholder={p.defaultValue || `%%${p.param}%%`}
                        value={paramValues[p.param] || ''}
                        onChange={e => setParamValues(prev => ({ ...prev, [p.param]: e.target.value }))}
                      />
                      <small className="text-muted">{p.name}{p.description ? ` — ${p.description}` : ''}</small>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email-превью */}
            {isEmailChannel && emailPreviewHtml && (
              <div className="col-12">
                <Form.Label className="small fw-semibold">Превью email-шаблона</Form.Label>
                <Card style={{ background: '#1a1a2e', borderRadius: 8, overflow: 'hidden' }}>
                  <Card.Body style={{ padding: 0 }}>
                    <div style={{ transform: 'scale(0.5)', transformOrigin: 'top center', height: 400 }}>
                      <div dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
                    </div>
                  </Card.Body>
                </Card>
                <div className="mt-2 d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={() => setShowEmailPreview(true)}>
                    Полноэкранный просмотр
                  </Button>
                  <Button variant="outline-secondary" size="sm" onClick={() => { navigator.clipboard.writeText(emailPreviewHtml); alert('HTML скопирован'); }}>
                    Копировать HTML
                  </Button>
                </div>
              </div>
            )}

            {/* Сообщение */}
            <div className="col-12">
              <Form.Label className="small fw-semibold">
                Сообщение
                {isEmailChannel && !selectedEmailPreset && <small className="text-muted ms-2">(произвольный текст для email)</small>}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                size="sm"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={isEmailChannel ? 'Введите текст email-сообщения...' : 'Введите текст сообщения...'}
              />
            </div>

            <div className="col-12 d-flex justify-content-end gap-2">
              {isEmailChannel && <small className="text-muted align-self-center me-auto">Канал: email | Отправитель: {senderEmail}</small>}
              <Button variant="primary" size="sm" onClick={sendMessage} disabled={sending}>
                {sending ? 'Отправка...' : 'Отправить'}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Модал полноэкранного превью */}
      <Modal show={showEmailPreview} onHide={() => setShowEmailPreview(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#1a1a2e', borderBottom: '1px solid #333' }}>
          <Modal.Title style={{ color: '#fff', fontSize: 14 }}>
            Превью: {selectedEmailPreset ? (EMAIL_PRESET_LABELS[selectedEmailPreset] || selectedEmailPreset) : 'Email'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, background: '#1a1a2e' }}>
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: '#1a1a2e', borderTop: '1px solid #333' }}>
          <Button size="sm" variant="outline-light" onClick={() => { navigator.clipboard.writeText(emailPreviewHtml); }}>Копировать HTML</Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setShowEmailPreview(false)}>Закрыть</Button>
        </Modal.Footer>
      </Modal>

      {/* Модал выбора пользователя */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Выберите пользователя</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              size="sm"
              placeholder="Поиск по имени, ID..."
              value={userSearchInput}
              onChange={e => setUserSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleUserSearch(); }}
            />
            <Button variant="outline-secondary" size="sm" onClick={handleUserSearch} disabled={userLoading}>
              {userLoading ? '...' : 'Найти'}
            </Button>
          </div>
          <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
            <thead className="table-light">
              <tr>
                <th>Имя</th>
                <th>ID</th>
                <th>Телефон</th>
                <th>Email</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {userLoading ? (
                <tr><td colSpan={5} className="text-muted text-center py-3">Загрузка пользователей с KYC-данными...</td></tr>
              ) : userList.length === 0 ? (
                <tr><td colSpan={5} className="text-muted text-center py-3">Нет пользователей с заполненными контактами</td></tr>
              ) : userList.map(user => {
                const hasContact = user.phone || user.email || user.kyc_phone || user.kyc_email;
                return (
                  <tr key={user.userId || user.id} onClick={() => handleSelectUser(user)}
                    style={{ cursor: 'pointer', opacity: hasContact ? 1 : 0.5 }}>
                    <td>{user.kyc_first_name ? `${user.kyc_first_name} ${user.kyc_last_name || ''}`.trim() : (user.name || '—')}</td>
                    <td><small className="text-muted">{(user.userId || user.id || '').slice(0, 8)}</small></td>
                    <td>{user.phone || user.kyc_phone || <span className="text-muted">—</span>}</td>
                    <td>{user.email || user.kyc_email || <span className="text-muted">—</span>}</td>
                    <td>
                      <Badge bg={user.status === 'active' || user.status === 'confirmed' ? 'success' : user.status === 'blocked' ? 'danger' : 'secondary'}>
                        {user.status || '—'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Всего: {userTotal} | Стр. {userPage} из {totalPages}
            </small>
            <div className="d-flex gap-1">
              <Button variant="outline-secondary" size="sm" disabled={userPage <= 1 || userLoading}
                onClick={() => fetchUsers(userSearchInput, userPage - 1)}>
                ←
              </Button>
              <Button variant="outline-secondary" size="sm" disabled={userPage >= totalPages || userLoading}
                onClick={() => fetchUsers(userSearchInput, userPage + 1)}>
                →
              </Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowUserModal(false)}>Закрыть</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MessageSender;
